import React, { useState } from 'react';
import { registerDriver, login } from '../api';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

const validateRut = (rut) => {
  if (!rut || typeof rut !== 'string') return false;
  const cleanRut = rut.replace(/[^0-9kK]/g, '');
  if (cleanRut.length < 8) return false;

  const body = cleanRut.slice(0, -1);
  const dv = cleanRut.slice(-1).toUpperCase();

  let sum = 0;
  let mul = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body.charAt(i), 10) * mul;
    mul = mul === 7 ? 2 : mul + 1;
  }

  const dvr = 11 - (sum % 11);
  const expectedDv = dvr === 11 ? '0' : dvr === 10 ? 'K' : String(dvr);

  return expectedDv === dv;
};

const formatRut = (rut) => {
  const value = rut.replace(/[^0-9kK]/g, '');
  if (value.length <= 1) return value;
  const body = value.slice(0, -1);
  const dv = value.slice(-1).toUpperCase();

  let formattedBody = '';
  let count = 0;
  for (let i = body.length - 1; i >= 0; i--) {
    formattedBody = body.charAt(i) + formattedBody;
    count++;
    if (count % 3 === 0 && i !== 0) {
      formattedBody = '.' + formattedBody;
    }
  }
  return `${formattedBody}-${dv}`;
};

const sanitizeForeignDocument = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  return raw.replace(/[^0-9a-zA-Z]/g, '').toUpperCase().slice(0, 20);
};

const validateForeignDocument = (doc) => {
  if (!doc || typeof doc !== 'string') return false;
  const cleaned = sanitizeForeignDocument(doc);
  return cleaned.length >= 5 && cleaned.length <= 20;
};

const openPolicyLink = (e, filename) => {
  e.preventDefault();
  const url = `https://transportesemilia.ajisolutionsspa.cl/${filename}`;
  const isCapacitor = window.Capacitor || (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.bridge);
  if (isCapacitor) {
    window.open(url, '_system');
  } else {
    window.open(url, '_blank');
  }
};

const RegisterDriver = () => {
  const [docCountryMode, setDocCountryMode] = useState('CL'); // 'CL' (RUT Chile) | 'INTL' (DNI / Extranjero sin guion)
  const [fullName, setFullName] = useState('');
  const [rut, setRut] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [formError, setFormError] = useState('');

  const handleCountryModeChange = (mode) => {
    setDocCountryMode(mode);
    setFormError('');
    if (rut) {
      setRut(mode === 'CL' ? formatRut(rut) : sanitizeForeignDocument(rut));
    }
  };

  const handleDocumentChange = (e) => {
    setFormError('');
    const raw = e.target.value;
    if (docCountryMode === 'CL') {
      setRut(formatRut(raw));
    } else {
      setRut(sanitizeForeignDocument(raw));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!acceptTerms) {
      const msg = 'Debes aceptar los Términos y Condiciones y la Política de Privacidad para continuar.';
      setFormError(msg);
      toast.warning(msg);
      return;
    }
    if (password !== confirmPassword) {
      const msg = 'Las contraseñas no coinciden.';
      setFormError(msg);
      toast.error(msg);
      return;
    }

    if (docCountryMode === 'CL') {
      if (!validateRut(rut)) {
        const msg = 'El RUT chileno ingresado no es válido. Si eres de Argentina u otro país, selecciona "Otro país (DNI / Pasaporte)".';
        setFormError(msg);
        toast.error(msg);
        return;
      }
    } else {
      if (!validateForeignDocument(rut)) {
        const msg = 'Ingresa un número de DNI / Documento válido (entre 5 y 20 caracteres, sin puntos ni guion).';
        setFormError(msg);
        toast.error(msg);
        return;
      }
    }

    const finalDocument = docCountryMode === 'CL' ? rut.trim() : sanitizeForeignDocument(rut);

    setIsLoading(true);
    try {
      await registerDriver({
        full_name: fullName.trim(),
        rut: finalDocument,
        phone: phone.trim(),
        email: email.trim(),
        password: password
      });
      toast.success('¡Registro exitoso! Iniciando sesión...');
      await login(email.trim(), password);
    } catch (err) {
      console.error("Registration error:", err);
      const msg = err.response?.data?.detail || 'Error al registrar conductor. Inténtalo de nuevo.';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mt-5 mb-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card shadow-sm border-0">
            <div className="card-body p-4">
              <div className="text-center mb-4">
                <img src="/EmiGo.png" alt="EmiGo Logo" style={{ maxWidth: '120px' }} />
                <h3 className="card-title mt-2 fw-bold">Registro Conductor</h3>
                <p className="text-muted">Únete a nuestra flota de conductores</p>
              </div>

              {formError && (
                <div role="alert" aria-live="assertive" className="alert alert-danger py-2 small">
                  {formError}
                </div>
              )}

              <form method="post" action="/auth/register-driver" onSubmit={handleSubmit}>
                <div className="form-group mb-3">
                  <label htmlFor="reg-full-name" className="fw-semibold small text-muted mb-1">
                    Nombre Completo
                  </label>
                  <input
                    type="text"
                    id="reg-full-name"
                    name="name"
                    autoComplete="name"
                    enterKeyHint="next"
                    className="form-control py-2"
                    placeholder="Ej. Juan Pérez"
                    value={fullName}
                    onChange={(e) => { setFormError(''); setFullName(e.target.value); }}
                    required
                    disabled={isLoading}
                  />
                </div>

                <fieldset className="mb-3 border rounded p-2 bg-light">
                  <legend className="float-none w-auto px-1 small fw-semibold text-muted mb-1" style={{ fontSize: '0.82rem' }}>
                    País de Identificación
                  </legend>
                  <div className="d-flex flex-wrap gap-3 px-1">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="doc_country_mode"
                        id="doc-mode-cl"
                        value="CL"
                        checked={docCountryMode === 'CL'}
                        onChange={() => handleCountryModeChange('CL')}
                        disabled={isLoading}
                      />
                      <label className="form-check-label small fw-semibold" htmlFor="doc-mode-cl">
                        🇨🇱 Chile (RUT)
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="doc_country_mode"
                        id="doc-mode-intl"
                        value="INTL"
                        checked={docCountryMode === 'INTL'}
                        onChange={() => handleCountryModeChange('INTL')}
                        disabled={isLoading}
                      />
                      <label className="form-check-label small fw-semibold" htmlFor="doc-mode-intl">
                        🌎 Otro país (DNI / CPF / Pasaporte)
                      </label>
                    </div>
                  </div>
                </fieldset>

                <div className="form-group mb-3">
                  <label htmlFor="reg-document-id" className="fw-semibold small text-muted mb-1">
                    {docCountryMode === 'CL' ? 'RUT (Chile)' : 'Número de DNI / Documento (sin puntos ni guion)'}
                  </label>
                  <input
                    type="text"
                    id="reg-document-id"
                    name="document_id"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    enterKeyHint="next"
                    aria-describedby="reg-document-help"
                    className="form-control py-2"
                    placeholder={docCountryMode === 'CL' ? '12.345.678-9' : 'Ej: 35890123'}
                    value={rut}
                    onChange={handleDocumentChange}
                    required
                    disabled={isLoading}
                  />
                  <small id="reg-document-help" className="form-text text-muted" style={{ fontSize: '0.78rem' }}>
                    {docCountryMode === 'CL'
                      ? 'Se formatea automáticamente con puntos y guion verificador.'
                      : 'Ingresa solo los números o letras de tu DNI, CPF, Cédula o Pasaporte (sin guion).'}
                  </small>
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="reg-phone" className="fw-semibold small text-muted mb-1">
                    Teléfono (con código de país)
                  </label>
                  <input
                    type="tel"
                    id="reg-phone"
                    name="phone"
                    autoComplete="tel"
                    enterKeyHint="next"
                    className="form-control py-2"
                    placeholder={docCountryMode === 'CL' ? '+56912345678' : '+5491123456789'}
                    value={phone}
                    onChange={(e) => { setFormError(''); setPhone(e.target.value); }}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="reg-email" className="fw-semibold small text-muted mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="reg-email"
                    name="email"
                    autoComplete="username"
                    spellCheck={false}
                    enterKeyHint="next"
                    className="form-control py-2"
                    placeholder="correo@ejemplo.com"
                    value={email}
                    onChange={(e) => { setFormError(''); setEmail(e.target.value); }}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label htmlFor="reg-new-password" className="fw-semibold small text-muted mb-0">
                      Contraseña
                    </label>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none small"
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword(prev => !prev)}
                    >
                      {showPassword ? 'Ocultar' : 'Mostrar'}
                    </button>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="reg-new-password"
                    name="new-password"
                    autoComplete="new-password"
                    enterKeyHint="next"
                    className="form-control py-2"
                    value={password}
                    onChange={(e) => { setFormError(''); setPassword(e.target.value); }}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group mb-4">
                  <label htmlFor="reg-confirm-password" className="fw-semibold small text-muted mb-1">
                    Confirmar Contraseña
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="reg-confirm-password"
                    name="confirm-password"
                    autoComplete="new-password"
                    enterKeyHint="done"
                    className="form-control py-2"
                    value={confirmPassword}
                    onChange={(e) => { setFormError(''); setConfirmPassword(e.target.value); }}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="acceptTerms"
                    name="accept_terms"
                    checked={acceptTerms}
                    onChange={(e) => { setFormError(''); setAcceptTerms(e.target.checked); }}
                    required
                    disabled={isLoading}
                  />
                  <label className="form-check-label small text-muted" htmlFor="acceptTerms">
                    Acepto los{' '}
                    <a
                      href="https://transportesemilia.ajisolutionsspa.cl/terminos-y-condiciones.html"
                      onClick={(e) => openPolicyLink(e, 'terminos-y-condiciones.html')}
                      className="text-decoration-none fw-semibold"
                    >
                      Términos y Condiciones
                    </a>{' '}
                    y la{' '}
                    <a
                      href="https://transportesemilia.ajisolutionsspa.cl/politica-privacidad.html"
                      onClick={(e) => openPolicyLink(e, 'politica-privacidad.html')}
                      className="text-decoration-none fw-semibold"
                    >
                      Política de Privacidad
                    </a>.
                  </label>
                </div>

                <button type="submit" className="btn btn-primary w-100 py-2.5 mt-3 fw-bold shadow-sm" disabled={isLoading}>
                  {isLoading ? 'Registrando...' : 'Crear Cuenta de Conductor'}
                </button>
              </form>

              <div className="text-center mt-4 border-top pt-3">
                <p className="mb-0 text-muted">
                  ¿Ya tienes una cuenta? <Link to="/" className="fw-semibold text-primary text-decoration-none">Inicia sesión aquí</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterDriver;

